import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Check, AlertCircle } from "lucide-react";

const NZ_REGIONS = [
  "Northland", "Auckland", "Waikato", "Bay of Plenty", "Gisborne", "Hawke's Bay",
  "Taranaki", "Manawatū-Whanganui", "Wellington", "Tasman", "Nelson", "Marlborough",
  "West Coast", "Canterbury", "Otago", "Southland",
];

export default function WaitlistForm({ compact = false }) {
  const [form, setForm] = useState({ name: "", email: "", region: "", postcode: "", box_size: "", frequency: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.name.trim().length < 2 || !form.email.includes("@")) {
      setErr("Please give us your name and a valid email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist", form);
      setDone(data.message || "You're on the list.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bloom-card p-10 text-center" data-testid="waitlist-success">
        <Check className="mx-auto mb-3 text-[var(--bloom-brand)]" strokeWidth={1.5} size={36} />
        <h3 className="font-serif text-3xl mb-2">You're on the list.</h3>
        <p className="font-serif italic text-[var(--bloom-ink-soft)] max-w-md mx-auto">{done}</p>
        <p className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mt-6">No payment yet. This just holds your spot.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bloom-card p-8 md:p-10" data-testid="waitlist-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Your name *">
          <input value={form.name} onChange={(e) => set("name", e.target.value)} className="bloom-input" placeholder="" data-testid="waitlist-name" />
        </Field>
        <Field label="Email *">
          <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bloom-input" placeholder="" data-testid="waitlist-email" />
        </Field>
        <Field label="Region / town">
          <select value={form.region} onChange={(e) => set("region", e.target.value)} className="bloom-input" data-testid="waitlist-region">
            <option value="">Select your region</option>
            {NZ_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Postcode">
          <input value={form.postcode} onChange={(e) => set("postcode", e.target.value)} className="bloom-input" placeholder="" data-testid="waitlist-postcode" />
        </Field>
        {!compact && (
          <>
            <Field label="Box you'd want">
              <select value={form.box_size} onChange={(e) => set("box_size", e.target.value)} className="bloom-input" data-testid="waitlist-box">
                <option value="">Not sure yet</option>
                <option value="small">Small (~10 stems)</option>
                <option value="medium">Medium (~15 stems)</option>
                <option value="large">Large (~25 stems)</option>
                <option value="unsure">Not sure yet</option>
              </select>
            </Field>
            <Field label="How often">
              <select value={form.frequency} onChange={(e) => set("frequency", e.target.value)} className="bloom-input" data-testid="waitlist-frequency">
                <option value="">Not sure yet</option>
                <option value="weekly">Weekly (12 deliveries)</option>
                <option value="fortnightly">Fortnightly (6)</option>
                <option value="monthly">Monthly (3)</option>
                <option value="unsure">Not sure yet</option>
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Anything else?">
                <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bloom-input" style={{ resize: "vertical" }} data-testid="waitlist-notes" />
              </Field>
            </div>
          </>
        )}
      </div>

      {err && (
        <div className="mt-4 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic" data-testid="waitlist-error">
          <AlertCircle size={14} /> {err}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)]">
          No payment yet. This just holds your spot in the queue.
        </p>
        <button type="submit" disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="waitlist-submit">
          {busy ? "Adding…" : "Add me to the waitlist"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="bloom-label mb-2">{label}</div>
      {children}
    </div>
  );
}
